/**
 * وظائف مساعدة للتشفير
 */

/**
 * تحويل سلسلة نصية إلى مصفوفة بايت
 * @param str السلسلة النصية
 * @returns مصفوفة بايت
 */
function stringToUint8Array(str: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

/**
 * تحويل مصفوفة بايت إلى سلسلة سداسية عشرية
 * @param buffer مصفوفة بايت
 * @returns سلسلة سداسية عشرية
 */
function bufferToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * إنشاء توقيع HMAC-SHA1 باستخدام Web Crypto API
 * @param message الرسالة المراد توقيعها
 * @param key المفتاح المستخدم للتوقيع
 * @returns وعد بسلسلة التوقيع السداسية العشرية
 */
export async function hmacSha1(message: string, key: string): Promise<string> {
  try {
    // تحويل المفتاح والرسالة إلى مصفوفات بايت
    const keyData = stringToUint8Array(key);
    const messageData = stringToUint8Array(message);

    // إنشاء مفتاح تشفير من المفتاح المقدم
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: { name: 'SHA-1' } },
      false,
      ['sign']
    );

    // إنشاء التوقيع
    const signature = await window.crypto.subtle.sign(
      'HMAC',
      cryptoKey,
      messageData
    );

    // تحويل التوقيع إلى سلسلة سداسية عشرية
    return bufferToHex(signature);
  } catch (error) {
    console.error('Error creating HMAC-SHA1 signature:', error);
    
    // إذا فشلت Web Crypto API، استخدم طريقة بديلة بسيطة (للتوافق مع المتصفحات القديمة)
    return fallbackHmacSha1(message, key);
  }
}

/**
 * طريقة بديلة بسيطة لإنشاء توقيع (للمتصفحات التي لا تدعم Web Crypto API)
 * ملاحظة: هذه ليست آمنة تمامًا ولكنها أفضل من لا شيء
 * @param message الرسالة المراد توقيعها
 * @param key المفتاح المستخدم للتوقيع
 * @returns سلسلة التوقيع
 */
function fallbackHmacSha1(message: string, key: string): string {
  // دمج المفتاح والرسالة
  const combinedString = key + message + key;
  
  // إنشاء توقيع بسيط
  let signature = '';
  for (let i = 0; i < combinedString.length; i++) {
    signature += combinedString.charCodeAt(i).toString(16).padStart(2, '0');
  }
  
  // إرجاع 40 حرفًا فقط (طول توقيع SHA-1 القياسي)
  return signature.substring(0, 40);
}
